import { useRef, useMemo, useCallback, useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { BottomNav } from "@/app/components/bottom-nav";
import { LoginPage } from "@/app/components/login-page";
import { LocationSelectorModal } from "@/app/components/location-selector-modal";
import { CommonHeader } from "@/app/components/common-header";
import { HomeSearchBar } from "@/app/components/home-search-bar";
import { PageLayout } from "@/app/components/page-layout";
import { AppRouter } from "@/app/components/app-router";
import { CartBlockToast } from "@/app/components/cart-block-toast";
import { OfferSentToast } from "@/app/components/offer-sent-toast";
import { Toaster } from "@/app/components/ui/sonner";
import { toast } from "sonner";
import { ChatModal } from "@/app/components/chat-modal";
import { FloatingChatButton } from "@/app/components/floating-chat-button";
import { Footer } from "@/app/components/footer";
import { Button } from "@/app/components/button";
import { Alert, AlertDescription, AlertTitle } from "@/app/components/ui/alert";
import { X } from "lucide-react";
import { useAppState } from "@/app/hooks/use-app-state";
import { useAppHandlers } from "@/app/hooks/use-app-handlers";
import { useNavigation } from "@/app/hooks/use-navigation";
import { CATEGORIES } from "@/app/data/initial-data";
import { getHomeFeed } from "@/app/api/auth-client";
import { getRecentCategories, getRecentSearches, pushRecentCategory, pushRecentSearch } from "@/app/utils/feed-personalization";
import { PromoCarousel, type PromoCarouselSlideConfig } from "@/app/components/promo-carousel";
import { loadPromoSlidesFromMobileShell } from "@/config/mobile-shell";
import { AdCard, type Ad } from "@/app/components/ad-card";
import { NewUserOnboarding, type OnboardingData } from "@/app/components/new-user-onboarding";
import { createSettingsNavigator } from "@/app/utils/settings-navigation";
import * as authClient from "@/app/api/auth-client";
import { getAuthConfig } from "@/config/auth";
import {
  profileToOnboardingData,
  profileAddressesToCheckoutAddresses,
  onboardingDataToProfilePatch,
  editProfileDataToProfilePatch,
  isProfileIncomplete,
  isPostAdBlockedByUnverifiedEmail,
  type EditProfileFormData,
} from "@/app/utils/profile-mapper";
import type { Profile } from "@/app/api/auth-client";
import type { SignUpResult } from "@/app/components/login-page";
import { getSellerInitials } from "@/app/utils/offer-utils";
import { getAdImage } from "@/app/utils/ad-utils";
import type { PageType } from "@/app/hooks/use-app-state";
import type { OfferData } from "@/app/components/make-offer-page";
import type { Conversation } from "@/app/components/chat-list-page";
import { hydrateBuyerCartFromServer } from "@/app/utils/hydrate-buyer-cart";
import { parseAdIdFromDeepLinkParam, removeAdDeepLinkFromAddressBar } from "@/app/utils/ad-share";
import { AccountNavProvider } from "@/app/context/account-nav-context";
import {
  hasAdDeepLinkInUrl,
  removeNotifyLinkFromAddressBar,
  shouldSuppressCartForStripeReturnUrl,
} from "@/app/utils/client-bootstrap-route";
import { clearSessionAppRoute, removeAuthLandingQueriesFromAddressBar } from "@/app/utils/session-app-route";
import { trackCampaignClickIfPresent } from "@/app/utils/campaign-tracking";
import {
  clearPendingAuthPage,
  consumePendingAuthPage,
  requiresAuth,
  savePendingAuthPage,
} from "@/app/utils/auth-required-route";

/** Only GET /auth/me/profile returning 404 means the user has no profile row yet. Other failures (5xx, network, 401 after refresh) must not send them to onboarding. */
function isProfileNotFoundError(e: unknown): boolean {
  return typeof e === "object" && e !== null && (e as { status?: number }).status === 404;
}

function collectOfferRelatedAdIds(offers: authClient.ApiOffer[]): Set<string> {
  const ids = new Set<string>();
  for (const o of offers) {
    if (o.ad_id?.trim()) ids.add(o.ad_id.trim());
    for (const pid of o.product_ad_ids ?? []) {
      if (typeof pid === "string" && pid.trim()) ids.add(pid.trim());
    }
  }
  return ids;
}

/** Resolve target + trade-in ads for offer lists (includes ads outside home feed). */
async function enrichAdsForOfferMapping(
  offers: authClient.ApiOffer[],
  feedAds: Ad[],
  myAdsApi: authClient.ApiAdResponse[]
): Promise<Ad[]> {
  const adsById = new Map<string, Ad>(feedAds.map((a) => [a.id, a]));
  myAdsApi.map((a) => authClient.apiAdToAd(a)).forEach((a) => adsById.set(a.id, a));
  await Promise.all(
    [...collectOfferRelatedAdIds(offers)].map((id) =>
      adsById.has(id)
        ? Promise.resolve()
        : authClient
            .getAdById(id)
            .then((api) => adsById.set(api.ad_id, authClient.apiAdToAd(api)))
            .catch(() => {})
    )
  );
  return Array.from(adsById.values());
}

export default function App() {
  const state = useAppState([]);
  const navigation = useNavigation(state);
  const handlers = useAppHandlers(state, navigation);
  const productsRef = useRef<HTMLDivElement>(null);
  const [myAdsRefreshKey, setMyAdsRefreshKey] = useState(0);
  /** Login screen opens in signup mode when true (e.g. header "Cadastrar"). */
  const [loginDefaultSignUp, setLoginDefaultSignUp] = useState(false);
  /** Hide incomplete-profile banner until next session after user dismisses. */
  const [incompleteProfileBannerDismissed, setIncompleteProfileBannerDismissed] = useState(false);
  const [homeFeedLoading, setHomeFeedLoading] = useState(true);
  /** `undefined` until first BFF load; then array from `ezyhub/mobile_shell` (or built-in default). */
  const [promoShellSlides, setPromoShellSlides] = useState<PromoCarouselSlideConfig[] | undefined>(undefined);
  const [myAdsForOffer, setMyAdsForOffer] = useState<Ad[]>([]);
  const [myAdsForOfferLoading, setMyAdsForOfferLoading] = useState(false);
  const [isSubmittingOffer, setSubmittingOffer] = useState(false);
  const [showOfferSentToast, setShowOfferSentToast] = useState(false);
  const offerSentOnCloseRef = useRef<() => void>(() => {});
  const processedStripeReturnOrderIdRef = useRef<string | null>(null);
  /** While true, session-restore must not apply GET /cart (stale) over cart cleared by Stripe return handler. */
  const suppressCartHydrationForStripeReturnRef = useRef(shouldSuppressCartForStripeReturnUrl());
  /** True from first paint if ?ad= was consumed by useAppState bootstrap (skip duplicate navigate). */
  const consumedAdDeepLinkRef = useRef(hasAdDeepLinkInUrl());

  // Strip ?ad= / ?notify_link= before paint when route was bootstrapped from share or offer email link.
  useLayoutEffect(() => {
    removeAdDeepLinkFromAddressBar();
    removeNotifyLinkFromAddressBar();
  }, []);

  // When on category-results, fetch server-side search (BFF expands category_id via catalog; no need to wait for client catalog).
  // useLayoutEffect so we clear stale rows before paint (avoids flashing previous banner/category results for 1–2s).
  useLayoutEffect(() => {
    if (state.currentPage !== "category-results") return;
    state.loadSearchResults();
  }, [state.currentPage, state.searchQuery, state.selectedCategory, state.loadSearchResults]);

  // When on home, fetch categories with ads (cached 10 min); refetch when stale or invalidated.
  const rawCategoriesTtl = import.meta.env.VITE_CATEGORIES_WITH_ADS_TTL_MS as string | undefined;
  const parsedCategoriesTtl = rawCategoriesTtl != null && rawCategoriesTtl !== "" ? Number(rawCategoriesTtl) : NaN;
  const CATEGORIES_WITH_ADS_TTL_MS =
    Number.isFinite(parsedCategoriesTtl) && parsedCategoriesTtl >= 0 ? parsedCategoriesTtl : 10 * 60 * 1000;
  useEffect(() => {
    if (state.currentPage !== "home") return;
    const stale =
      state.categoriesWithAdsFetchedAt != null &&
      Date.now() - state.categoriesWithAdsFetchedAt > CATEGORIES_WITH_ADS_TTL_MS;
    if (state.categoriesWithAds !== null && !stale) return;
    state.loadCategoriesWithAds();
  }, [
    state.currentPage,
    state.categoriesWithAds,
    state.categoriesWithAdsFetchedAt,
    state.loadCategoriesWithAds,
  ]);

  // When on category-results without a catalog (e.g. refresh or direct link), load catalog for labels (BFF already expands category for /ads/search).
  useEffect(() => {
    if (state.currentPage !== "category-results") return;
    if (state.categoryCatalog != null) return;
    state.loadCategoriesWithAds();
  }, [state.currentPage, state.categoryCatalog, state.loadCategoriesWithAds]);

  // Load home feed on mount (replaces mocked INITIAL_ADS). Phase 2: send stored recent_categories/recent_searches for personalization.
  useEffect(() => {
    let cancelled = false;
    setHomeFeedLoading(true);
    getHomeFeed({
      recent_categories: getRecentCategories(state.userId),
      recent_searches: getRecentSearches(state.userId),
    })
      .then((ads) => {
        if (!cancelled) {
          state.setAds(ads);
        }
      })
      .catch(() => {
        if (!cancelled) {
          state.setAds([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHomeFeedLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [state.setAds, state.userId]);

  // Home promo carousel: config-platform → BFF GET /v1/mobile/shell (cached server + localStorage in mobile-shell.ts).
  useEffect(() => {
    let cancelled = false;
    void loadPromoSlidesFromMobileShell().then((slides) => {
      if (!cancelled) setPromoShellSlides(slides);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load current user's ads when on make-offer so "Produto + Dinheiro" has the correct list (home feed does not include own ads)
  useEffect(() => {
    if (state.currentPage !== "make-offer" || !state.userId) {
      setMyAdsForOffer([]);
      setMyAdsForOfferLoading(false);
      return;
    }
    let cancelled = false;
    setMyAdsForOfferLoading(true);
    Promise.all([authClient.getMyAds(), authClient.getCategories().catch(() => [])]).then(([list, categories]) => {
      if (cancelled) return;
      const categoryNameById = new Map<string, string>();
      for (const c of categories) {
        categoryNameById.set(c.category_id, c.name);
      }
      const ads = list.map((api) => {
        const ad = authClient.apiAdToAd(api);
        if (!ad.category_tree && ad.category && categoryNameById.has(ad.category)) {
          return { ...ad, category: categoryNameById.get(ad.category)! };
        }
        return ad;
      });
      setMyAdsForOffer(ads);
    }).catch(() => {
      if (!cancelled) setMyAdsForOffer([]);
    }).finally(() => {
      if (!cancelled) setMyAdsForOfferLoading(false);
    });
    return () => { cancelled = true; };
  }, [state.currentPage, state.userId, myAdsRefreshKey]);

  const handleCreateAdWithRefresh = useCallback(
    (payload: Parameters<typeof handlers.handleCreateAd>[0]) => {
      handlers.handleCreateAd(payload);
      setMyAdsRefreshKey((k) => k + 1);
      state.invalidateCategoriesWithAds();
    },
    [handlers.handleCreateAd, state.invalidateCategoriesWithAds]
  );

  // Handler for carousel category clicks (slide may use a leaf category_id; label resolves parent section in search)
  const handleCarouselCategoryClick = useCallback((categoryId: string) => {
    pushRecentCategory(categoryId, state.userId);
    state.setSelectedCategory(categoryId);
    navigation.navigateToPage("category-results");
  }, [state.userId, state.setSelectedCategory, navigation.navigateToPage]);

  const handleCarouselNavigateToTrocaViaHub = useCallback(() => {
    navigation.navigateToPage("troca-via-hub");
  }, [navigation.navigateToPage]);

  // Handler for home search button click
  const handleHomeSearch = useCallback(() => {
    // Reset category to "Todos" when searching from home
    state.setSelectedCategory("Todos");
    navigation.navigateToPage("category-results");
  }, [state.setSelectedCategory, navigation.navigateToPage]);

  // Handler for back to home - reset search
  const handleBackToHome = useCallback(() => {
    state.setSearchQuery("");
    state.setSelectedCategory("Todos");
    state.setCurrentPage("home");
    // Clear navigation history when going to home
    state.setNavigationHistory([]);
  }, [state.setSearchQuery, state.setSelectedCategory, state.setCurrentPage, state.setNavigationHistory]);

  // Handler for search changes - navigate to results page
  const handleSearchChange = useCallback((query: string) => {
    if (query.trim() !== "") pushRecentSearch(query.trim(), state.userId);
    state.setSearchQuery(query);
    if (query.trim() !== "" || state.selectedCategory !== "Todos") {
      navigation.navigateToPage("category-results");
    }
  }, [state.userId, state.setSearchQuery, state.selectedCategory, navigation.navigateToPage]);

  // Handler for category changes - navigate to results page
  const handleCategoryChange = useCallback((category: string) => {
    pushRecentCategory(category, state.userId);
    state.setSelectedCategory(category);
    if (category !== "Todos" || state.searchQuery.trim() !== "") {
      navigation.navigateToPage("category-results");
    }
  }, [state.userId, state.setSelectedCategory, state.searchQuery, navigation.navigateToPage]);

  // Calculate total unread messages - memoized
  const totalUnreadMessages = useMemo(() => 
    state.conversations.reduce((sum, conv) => sum + conv.unreadCount, 0),
    [state.conversations]
  );

  // Calculate active custom filters count - memoized
  const activeCustomFiltersCount = useMemo(() =>
    state.customFilters.brands.length +
    state.customFilters.conditions.length +
    state.customFilters.locations.length +
    (state.customFilters.priceMin ? 1 : 0) +
    (state.customFilters.priceMax ? 1 : 0),
    [state.customFilters]
  );

  // Handler to clear all custom filters
  const handleClearCustomFilters = useCallback(() => {
    state.setCustomFilters({
      brands: [],
      conditions: [],
      priceMin: "",
      priceMax: "",
      locations: [],
    });
  }, [state.setCustomFilters]);

  const openLoginSignIn = useCallback(() => {
    setLoginDefaultSignUp(false);
    state.setCurrentPage("login");
  }, [state.setCurrentPage]);

  const openLoginSignUp = useCallback(() => {
    setLoginDefaultSignUp(true);
    state.setCurrentPage("login");
  }, [state.setCurrentPage]);

  /** Create ad / open modal — requires login + completed profile (or onboarding). */
  const handleRequestCreateAd = useCallback(() => {
    if (!state.isLoggedIn) {
      setLoginDefaultSignUp(false);
      state.setCurrentPage("login");
      return;
    }
    if (isProfileIncomplete(state.userProfile)) {
      toast.message("Complete seu cadastro", {
        description: "Precisamos de alguns dados para você anunciar e concluir compras com segurança.",
      });
      state.setNeedsOnboarding(true);
      return;
    }
    if (isPostAdBlockedByUnverifiedEmail(state.userProfile)) {
      toast.message("Confirme seu e-mail", {
        description:
          "Abra o link que enviamos para sua caixa de entrada (e o spam) ou acesse Perfil para reenviar a confirmação.",
      });
      return;
    }
    state.setIsModalOpen(true);
  }, [
    state.isLoggedIn,
    state.userProfile,
    state.setCurrentPage,
    state.setNeedsOnboarding,
    state.setIsModalOpen,
  ]);

  // Common props for pages with bottom navigation - memoized
  const commonPageProps = useMemo(
    () => ({
      activeTab: (state.currentPage === "favorites"
        ? "favorites"
        : state.currentPage === "wallet"
          ? "wallet"
          : "home") as "home" | "favorites" | "wallet",
      onTabChange: (tab: string) => navigation.navigateToPage(tab as PageType),
      favoritesCount: state.favoriteAdIds.length,
      onMenuClick: () => state.setIsMenuOpen(true),
      onPostAdClick: handleRequestCreateAd,
      isLoggedIn: state.isLoggedIn,
      onLoginClick: openLoginSignIn,
      onSignUpClick: openLoginSignUp,
      postAdBlockedByUnverifiedEmail: isPostAdBlockedByUnverifiedEmail(state.userProfile),
    }),
    [
      state.currentPage,
      state.favoriteAdIds.length,
      state.setIsMenuOpen,
      state.isLoggedIn,
      state.userProfile,
      navigation.navigateToPage,
      handleRequestCreateAd,
      openLoginSignIn,
      openLoginSignUp,
    ]
  );

  // Memoize callbacks that are passed as props
  const handleAdClick = useCallback((adId: string) => {
    state.setSelectedAdId(adId);
    navigation.navigateToPage("ad-detail");
  }, [state.setSelectedAdId, navigation.navigateToPage]);

  const handleOpenOrderDetail = useCallback((orderId: string, returnPage: "purchases" | "sales" = "purchases") => {
    state.setOrderDetailReturnPage(returnPage);
    state.setSelectedOrderId(orderId);
    navigation.navigateToPage("order-detail");
  }, [state.setOrderDetailReturnPage, state.setSelectedOrderId, navigation.navigateToPage]);

  const handleCartClick = useCallback(() => navigation.navigateToPage("cart"), [navigation.navigateToPage]);
  const handleNotificationsClick = useCallback(() => navigation.navigateToPage("notifications"), [navigation.navigateToPage]);
  const handleAddressClick = useCallback(() => navigation.navigateToPage("addresses"), [navigation.navigateToPage]);
  const handleChatClick = useCallback(() => {
    state.setSelectedConversationId(null);
    state.setIsChatModalOpen(true);
  }, [state.setSelectedConversationId, state.setIsChatModalOpen]);
  
  const handleCloseModal = useCallback(() => state.setIsModalOpen(false), [state.setIsModalOpen]);
  const handleOpenModal = useCallback(() => handleRequestCreateAd(), [handleRequestCreateAd]);
  const handleCloseMenu = useCallback(() => state.setIsMenuOpen(false), [state.setIsMenuOpen]);
  const handleCloseCartToast = useCallback(() => state.setShowCartToast(false), [state.setShowCartToast]);
  const handleLocationSelectorOpen = useCallback(() => state.setIsLocationSelectorOpen(true), [state.setIsLocationSelectorOpen]);
  const handleLocationSelectorClose = useCallback(() => state.setIsLocationSelectorOpen(false), [state.setIsLocationSelectorOpen]);
  const handleSortSheetOpen = useCallback(() => state.setIsSortSheetOpen(true), [state.setIsSortSheetOpen]);
  const handleSortSheetClose = useCallback(() => state.setIsSortSheetOpen(false), [state.setIsSortSheetOpen]);
  const handleCustomFilterOpen = useCallback(() => state.setIsCustomFilterOpen(true), [state.setIsCustomFilterOpen]);
  const handleCustomFilterClose = useCallback(() => state.setIsCustomFilterOpen(false), [state.setIsCustomFilterOpen]);
  const handleClearNavigatedFromMenu = useCallback(() => state.setNavigatedFromMenu(false), [state.setNavigatedFromMenu]);

  // Handler for navigating back to settings from sub-pages (like payment methods or password security)
  const handleNavigateBackToSettings = useCallback(() => {
    const shouldRestoreMenu = state.navigatedFromMenu;
    
    // Pop the last page from history (the sub-page we're coming from)
    if (state.navigationHistory.length > 0) {
      const history = [...state.navigationHistory];
      history.pop(); // Remove the sub-page from history
      state.setNavigationHistory(history);
    }
    
    state.setCurrentPage("settings");
    
    // Restore menu state if navigation came from menu
    if (shouldRestoreMenu) {
      setTimeout(() => {
        state.setIsMenuOpen(true);
        state.setNavigatedFromMenu(false);
      }, 0);
    }
  }, [state]);

  const resolveBackFallback = useCallback((): PageType => {
    switch (state.currentPage) {
      case "checkout-shipping":
        return "checkout-address";
      case "checkout-review":
        return "checkout-shipping";
      case "checkout-address":
      case "cart":
        return "home";
      case "order-detail":
        return state.orderDetailReturnPage === "sales" ? "sales" : "purchases";
      case "password-security":
      case "payment-methods":
      case "language-region":
      case "privacy":
      case "data-usage":
      case "about":
      case "terms":
      case "privacy-policy":
        return "settings";
      case "account-deletion":
        return "home";
      default:
        return "home";
    }
  }, [state.currentPage, state.orderDetailReturnPage]);

  // Back: use return context when set (ad-detail from list, checkout steps); otherwise safe back with contextual fallback.
  const handleBack = useCallback(() => {
    if (state.returnToPage != null) {
      if (state.returnToPage === state.currentPage) {
        state.setReturnToPage(null);
        navigation.safeBack(resolveBackFallback());
      } else {
        const target = state.returnToPage;
        state.setReturnToPage(null);
        state.setCurrentPage(target);
        // Checkout funnel: restore previous step so chained Back works (review→shipping→address→cart).
        if (target === "checkout-shipping") {
          state.setReturnToPage("checkout-address");
        } else if (target === "checkout-address") {
          state.setReturnToPage("cart");
        }
        if (state.navigatedFromMenu) {
          setTimeout(() => {
            state.setIsMenuOpen(true);
            state.setNavigatedFromMenu(false);
          }, 0);
        }
      }
    } else {
      navigation.safeBack(resolveBackFallback());
    }
  }, [state.returnToPage, state.currentPage, state.setReturnToPage, state.setCurrentPage, state.navigatedFromMenu, state.setIsMenuOpen, state.setNavigatedFromMenu, navigation, resolveBackFallback]);

  // Handlers for navigating to settings sub-pages - using utility
  const settingsNavigators = useMemo(
    () => createSettingsNavigator(state.setNavigatedFromMenu, navigation.navigateToPage),
    [state.setNavigatedFromMenu, navigation.navigateToPage]
  );
  
  const handleMakeOffer = useCallback(() => {
    if (!state.isLoggedIn) {
      toast.error("Por favor, faça login para fazer uma oferta");
      return;
    }
    navigation.navigateToPage("make-offer");
  }, [state.isLoggedIn, navigation]);
  
  const handleViewOfferDetails = useCallback((offerId: string, sender: "user" | "other") => {
    state.setIsChatModalOpen(false);
    state.setSelectedOfferId(offerId);
    if (sender === "user") {
      navigation.navigateToPage("sent-offers");
    } else {
      navigation.navigateToPage("received-offers");
    }
  }, [state, navigation]);

  /** Navigate from a notification link (e.g. /offers/received?highlight=id, /purchases?order_id=id, /sales?order_id=id). */
  const handleNavigateToNotificationLink = useCallback((link: string) => {
    if (!link || typeof link !== "string") return;
    const pathPart = link.split("?")[0].replace(/^\//, "").toLowerCase();
    const search = link.includes("?") ? link.slice(link.indexOf("?")) : "";
    const params = search ? new URLSearchParams(search) : null;
    const highlight = params?.get("highlight") ?? null;
    const orderId = params?.get("order_id") ?? null;

    // PHASE_4 — Fire-and-forget campaign click tracking. Runs before the
    // route change so a quick app navigation can't cancel the request.
    if (params) {
      void trackCampaignClickIfPresent(params, { userId: state.userId ?? null });
    }

    if (pathPart === "offers/received") {
      state.setSelectedOfferId(highlight);
      navigation.navigateToPage("received-offers");
    } else if (pathPart === "offers/sent") {
      state.setSelectedOfferId(highlight);
      navigation.navigateToPage("sent-offers");
    } else if (pathPart === "purchases") {
      if (orderId) {
        state.setOrderDetailReturnPage("purchases");
        state.setSelectedOrderId(orderId);
        navigation.navigateToPage("order-detail");
      } else {
        navigation.navigateToPage("purchases");
      }
    } else if (pathPart === "sales") {
      if (orderId) {
        state.setOrderDetailReturnPage("sales");
        state.setSelectedOrderId(orderId);
        navigation.navigateToPage("order-detail");
      } else {
        navigation.navigateToPage("sales");
      }
    }
  }, [state, navigation]);

  /** Refetch notification unread count (for bell badge). Only called by the centralized refetch policy: visibility, opening notifications page, and every 5 min when tab visible. */
  const refetchNotificationCount = useCallback(() => {
    authClient
      .getNotificationsUnreadCount()
      .then((count) => state.setNotificationUnreadCount(count))
      .catch(() => {});
  }, [state.setNotificationUnreadCount]);

  const conversationsPollInFlightRef = useRef(false);

  /** Background refresh of chat list (unread counts for FAB). Silent; no toast on failure. */
  const refetchConversationsList = useCallback(() => {
    if (!state.isLoggedIn || !state.userId) return;
    if (!authClient.getAccessToken()) return;
    if (conversationsPollInFlightRef.current) return;
    conversationsPollInFlightRef.current = true;
    authClient
      .getConversations({ limit: 50 })
      .then((res) => {
        const list: Conversation[] = (res.items ?? []).map((item) =>
          authClient.apiConversationItemToConversation(item, state.userId)
        ) as Conversation[];
        state.setConversations(list);
      })
      .catch(() => {})
      .finally(() => {
        conversationsPollInFlightRef.current = false;
      });
  }, [state.isLoggedIn, state.userId, state.setConversations]);

  const handleSubmitOffer = useCallback(
    (offer: OfferData) => {
      const targetAdId = String(offer.targetAdId ?? "").trim();
      if (!targetAdId) {
        toast.error("Anúncio não encontrado. Volte ao anúncio e tente de novo.");
        return;
      }

      const targetAdFromFeed = state.ads.find((ad) => ad.id === targetAdId);
      const targetAdImage = offer.targetAdImage?.trim();
      const targetAd: Ad =
        targetAdFromFeed ??
        ({
          id: targetAdId,
          title: offer.targetAdTitle?.trim() || "Anúncio",
          description: "",
          price: typeof offer.targetAdPrice === "number" ? offer.targetAdPrice : 0,
          category: "",
          location: "",
          seller: offer.targetAdSeller,
          image: targetAdImage,
          imageUrl: targetAdImage,
          images: targetAdImage ? [targetAdImage] : undefined,
        } as Ad);

      const amountCents = Math.round(offer.cashAmount * 100);
      const apiType = offer.offerType === "cash-only" ? "money" : "product_money";
      const productAdIds: string[] = offer.tradeInAds?.map((t: { id: string }) => t.id).filter(Boolean) ?? [];

      if (offer.offerType === "cash-only" || (offer.offerType === "product-plus-cash" && productAdIds.length > 0)) {
        setSubmittingOffer(true);
        authClient
          .createOffer(targetAdId, apiType, amountCents, offer.message, productAdIds.length > 0 ? productAdIds : undefined)
          .then(async (apiOffer) => {
            let adsForMapping = state.ads;
            if (!adsForMapping.some((a) => a.id === targetAdId)) {
              try {
                const api = await authClient.getAdById(targetAdId);
                adsForMapping = [...adsForMapping, authClient.apiAdToAd(api)];
              } catch {
                adsForMapping = [...adsForMapping.filter((a) => a.id !== targetAdId), targetAd];
              }
            }
            const newSentOffer = authClient.mapApiOfferToSentOffer(apiOffer, adsForMapping, undefined, state.userId);
            state.setSentOffers([...state.sentOffers, newSentOffer]);
            setSubmittingOffer(false);
            offerSentOnCloseRef.current = () => {
              state.setSelectedAdId(offer.targetAdId);
              if (navigation.canGoBack) {
                navigation.navigateBack();
              } else {
                navigation.navigateToPage("ad-detail");
              }
            };
            setShowOfferSentToast(true);
          })
          .catch((err: Error & { status?: number }) => {
            setSubmittingOffer(false);
            if (err.status === 409) {
              toast.error(
                "Você já tem uma oferta pendente para este anúncio. Acesse Menu → Negociações → Ofertas Enviadas para ver ou acompanhar."
              );
            } else {
              toast.error(err?.message ?? "Não foi possível enviar a oferta.");
            }
          });
        return;
      }

      // Product-plus-cash with no products selected (should not happen if UI forces selection)
      const sellerName = targetAd.seller || "Vendedor";
      const conversationId = `seller-${offer.targetAdId}-${Date.now()}`;
      const offerId = `offer-${Date.now()}`;

      const existingConversation = state.conversations.find(
        (conv) => conv.type === "seller" && conv.adTitle === targetAd.title
      );

      if (!existingConversation) {
        state.setConversations([
          ...state.conversations,
          {
            id: conversationId,
            name: sellerName,
            lastMessage: "Nova oferta enviada",
            timestamp: new Date(),
            unreadCount: 0,
            type: "seller" as const,
            adTitle: targetAd.title,
          },
        ]);
      }

      const newSentOffer = {
        id: offerId,
        sellerName,
        sellerAvatar: getSellerInitials(sellerName),
        targetAd,
        offerType: offer.offerType,
        cashAmount: offer.cashAmount,
        tradeInAds: offer.tradeInAds?.map((t) => {
          const tradeAd =
            myAdsForOffer.find((a) => a.id === t.id) ?? state.ads.find((a) => a.id === t.id);
          return { ...t, image: tradeAd ? getAdImage(tradeAd) : "" };
        }),
        message: offer.message,
        totalOfferValue: offer.totalOfferValue,
        status: "pending" as const,
        createdAt: new Date(),
        updates: [
          { type: "sent" as const, message: "Oferta enviada ao vendedor", timestamp: new Date() },
        ],
      };
      state.setSentOffers([...state.sentOffers, newSentOffer]);

      const offerMessage = {
        id: `msg-${Date.now()}`,
        conversationId: existingConversation?.id ?? conversationId,
        text: offer.message ?? "Gostaria de fazer uma oferta pelo seu produto.",
        sender: "user" as const,
        timestamp: new Date(),
        offerData: {
          offerId,
          offerType: offer.offerType,
          totalOfferValue: offer.totalOfferValue,
          targetAdTitle: targetAd.title,
          targetAdId: offer.targetAdId,
          status: "pending" as const,
        },
      };
      state.setChatMessages([...state.chatMessages, offerMessage]);

      offerSentOnCloseRef.current = () => {
        state.setSelectedAdId(offer.targetAdId);
        if (navigation.canGoBack) {
          navigation.navigateBack();
        } else {
          navigation.navigateToPage("ad-detail");
        }
      };
      setShowOfferSentToast(true);
    },
    [state, navigation, myAdsForOffer]
  );

  /** Load sent offers from BFF and update state (used by Sent offers page). Enriches with my ads and trade-in product ads so tradeInAds is populated (needed for hub routing when adding offer to cart). */
  const loadSentOffers = useCallback(async () => {
    try {
      const [res, myAdsApi] = await Promise.all([
        authClient.getSentOffers(20),
        authClient.getMyAds().catch(() => [] as authClient.ApiAdResponse[]),
      ]);
      const adsForMapping = await enrichAdsForOfferMapping(res.items, state.ads, myAdsApi);
      const mapped = res.items.map((o) => authClient.mapApiOfferToSentOffer(o, adsForMapping, res.items, state.userId));
      state.setSentOffers(mapped);
    } catch {
      state.setSentOffers([]);
    }
  }, [state.ads, state.setSentOffers, state.userId]);

  /** Load received offers from BFF and update state (used by Received offers page). Returns the mapped list so callers can update local state immediately (e.g. after 400 "offer not pending"). */
  const loadReceivedOffers = useCallback(async () => {
    try {
      const [res, myAdsApi] = await Promise.all([
        authClient.getReceivedOffers(20),
        authClient.getMyAds().catch(() => [] as authClient.ApiAdResponse[]),
      ]);
      const adsForMapping = await enrichAdsForOfferMapping(res.items, state.ads, myAdsApi);
      const mapped = res.items.map((o) => authClient.mapApiOfferToReceivedOffer(o, adsForMapping, state.userId));
      state.setReceivedOffers(mapped);
      return mapped;
    } catch {
      state.setReceivedOffers([]);
      return [];
    }
  }, [state.ads, state.setReceivedOffers, state.userId]);

  // Memoize current location string
  const currentLocation = useMemo(() => 
    state.selectedCity ? `${state.selectedCity.name}, ${state.selectedCity.stateCode}` : "Franca, São Paulo",
    [state.selectedCity]
  );

  /** Footer / category-results "Categorias Populares": top 5 by active ad count when API provides rankings. */
  const FOOTER_POPULAR_TOP = 5;
  const popularCategories = useMemo(() => {
    const catalog = state.categoryCatalog ?? [];
    const ranked = state.categoriesWithAdsRanked;

    if (ranked && ranked.length > 0 && catalog.length > 0) {
      const out: { categoryId: string; name: string }[] = [];
      for (const row of ranked) {
        const name = catalog.find((c) => c.category_id === row.category_id)?.name?.trim();
        if (name) {
          out.push({ categoryId: row.category_id, name });
          if (out.length >= FOOTER_POPULAR_TOP) break;
        }
      }
      if (out.length > 0) return out;
    }

    const ids = state.categoriesWithAds ?? [];
    const withNames = ids
      .map((id) => ({
        categoryId: id,
        name: catalog.find((c) => c.category_id === id)?.name ?? "",
      }))
      .filter((c) => c.name);
    withNames.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    return withNames.slice(0, FOOTER_POPULAR_TOP);
  }, [state.categoriesWithAds, state.categoriesWithAdsRanked, state.categoryCatalog]);

  /** Promo carousel: same visibility rules as mobile (catalog membership + Troca-via-Hub exception). */
  const validCatalogCategoryIdsForPromo = useMemo(() => {
    const catalog = state.categoryCatalog;
    if (catalog == null) return null;
    return new Set(catalog.map((c) => c.category_id).filter(Boolean));
  }, [state.categoryCatalog]);

  /**
   * After a successful sign-in or onboarding completion, return the user to
   * the protected page they originally asked for (saved by the auth guard) or
   * fall back to Home when no intent is pending. Keeps deep links from email
   * working end-to-end without the user losing context.
   */
  const restorePostLoginPage = useCallback(() => {
    const pending = consumePendingAuthPage();
    state.setCurrentPage(pending ?? "home");
  }, [state.setCurrentPage]);

  // Handle email/password login (Bonamassa). After login, fetch profile to set needsOnboarding (404 => true, 200 => false).
  const handleLogin = useCallback(
    async (email: string, password: string) => {
      if (!authClient.isAuthConfigured()) {
        throw Object.assign(new Error("auth_not_configured"), { message: "auth_not_configured" });
      }
      await authClient.login(email, password);
      const user = await authClient.me();
      state.setUserId(user.user_id);
      state.setUserEmail(user.email);
      state.setUserName(user.name);
      state.setAuthEmailVerified(user.email_verified);
      state.setIsLoggedIn(true);
      try {
        const profile = await authClient.getProfile();
        state.setUserProfile(profileToOnboardingData(profile, user.email, user.name, user.email_verified));
        state.setUserProfilePicture(profile.profile_image_url ?? "");
        state.setNeedsOnboarding(false);
        const checkoutAddrs = profileAddressesToCheckoutAddresses(profile.addresses ?? []);
        state.setAddresses(checkoutAddrs);
        const mainAddr = checkoutAddrs.find((a) => a.isDefault) ?? checkoutAddrs[0];
        state.setSelectedAddressId(mainAddr ? mainAddr.id : null);
      } catch (e) {
        state.setUserProfile(null);
        state.setUserProfilePicture("");
        state.setNeedsOnboarding(isProfileNotFoundError(e));
      }
      try {
        const fav = await authClient.getFavorites();
        state.setFavoriteAdIds(fav.items.map((i) => i.ad_id));
        state.setFavoriteCreatedAt(
          fav.items.reduce((acc, i) => ({ ...acc, [i.ad_id]: i.created_at }), {} as Record<string, string>)
        );
      } catch {
        state.setFavoriteAdIds([]);
        state.setFavoriteCreatedAt({});
      }
      restorePostLoginPage();
    },
    [
      state.setUserId,
      state.setUserEmail,
      state.setUserName,
      state.setAuthEmailVerified,
      state.setIsLoggedIn,
      state.setUserProfile,
      state.setUserProfilePicture,
      state.setNeedsOnboarding,
      state.setFavoriteAdIds,
      state.setFavoriteCreatedAt,
      restorePostLoginPage,
    ]
  );

  // Handle signup (Bonamassa) - user must verify email before logging in
  const handleSignUp = useCallback(
    async (
      email: string,
      password: string,
      name: string,
      termsAccepted: boolean,
      privacyAccepted: boolean
    ): Promise<SignUpResult | void> => {
      if (!authClient.isAuthConfigured()) {
        throw Object.assign(new Error("auth_not_configured"), { message: "auth_not_configured" });
      }
      await authClient.signup(email, password, name, termsAccepted, privacyAccepted);
      state.setUserEmail(email);
      state.setUserName(name);
      return { verifyEmail: true };
    },
    [state.setUserEmail, state.setUserName]
  );

  // Handle Google SSO: login then fetch profile; 404 => needs onboarding, 200 => set profile and no onboarding.
  const handleGoogleSSO = useCallback(
    async (idToken: string) => {
      if (!authClient.isAuthConfigured()) {
        throw Object.assign(new Error("auth_not_configured"), { message: "auth_not_configured" });
      }
      await authClient.googleLogin(idToken);
      const user = await authClient.me();
      state.setUserId(user.user_id);
      state.setUserEmail(user.email);
      state.setUserName(user.name);
      state.setAuthEmailVerified(user.email_verified);
      state.setIsLoggedIn(true);
      try {
        const profile = await authClient.getProfile();
        state.setUserProfile(profileToOnboardingData(profile, user.email, user.name, user.email_verified));
        state.setUserProfilePicture(profile.profile_image_url ?? "");
        state.setNeedsOnboarding(false);
        const checkoutAddrs = profileAddressesToCheckoutAddresses(profile.addresses ?? []);
        state.setAddresses(checkoutAddrs);
        const mainAddr = checkoutAddrs.find((a) => a.isDefault) ?? checkoutAddrs[0];
        state.setSelectedAddressId(mainAddr ? mainAddr.id : null);
      } catch (e) {
        state.setUserProfile(null);
        state.setUserProfilePicture("");
        state.setNeedsOnboarding(isProfileNotFoundError(e));
      }
      try {
        const fav = await authClient.getFavorites();
        state.setFavoriteAdIds(fav.items.map((i) => i.ad_id));
        state.setFavoriteCreatedAt(
          fav.items.reduce((acc, i) => ({ ...acc, [i.ad_id]: i.created_at }), {} as Record<string, string>)
        );
      } catch {
        state.setFavoriteAdIds([]);
        state.setFavoriteCreatedAt({});
      }
      restorePostLoginPage();
    },
    [
      state.setUserId,
      state.setUserEmail,
      state.setUserName,
      state.setAuthEmailVerified,
      state.setUserProfilePicture,
      state.setIsLoggedIn,
      state.setUserProfile,
      state.setNeedsOnboarding,
      state.setFavoriteAdIds,
      state.setFavoriteCreatedAt,
      restorePostLoginPage,
    ]
  );

  // Handle Apple SSO: login then fetch profile; 404 => needs onboarding, 200 => set profile and no onboarding.
  const handleAppleSSO = useCallback(
    async (result: { idToken: string; email?: string; name?: string }) => {
      if (!authClient.isAuthConfigured()) {
        throw Object.assign(new Error("auth_not_configured"), { message: "auth_not_configured" });
      }
      await authClient.appleLogin(result.idToken, result.email, result.name);
      const user = await authClient.me();
      state.setUserId(user.user_id);
      state.setUserEmail(user.email);
      state.setUserName(user.name);
      state.setAuthEmailVerified(user.email_verified);
      state.setIsLoggedIn(true);
      try {
        const profile = await authClient.getProfile();
        state.setUserProfile(profileToOnboardingData(profile, user.email, user.name, user.email_verified));
        state.setUserProfilePicture(profile.profile_image_url ?? "");
        state.setNeedsOnboarding(false);
        const checkoutAddrs = profileAddressesToCheckoutAddresses(profile.addresses ?? []);
        state.setAddresses(checkoutAddrs);
        const mainAddr = checkoutAddrs.find((a) => a.isDefault) ?? checkoutAddrs[0];
        state.setSelectedAddressId(mainAddr ? mainAddr.id : null);
      } catch (e) {
        state.setUserProfile(null);
        state.setUserProfilePicture("");
        state.setNeedsOnboarding(isProfileNotFoundError(e));
      }
      try {
        const fav = await authClient.getFavorites();
        state.setFavoriteAdIds(fav.items.map((i) => i.ad_id));
        state.setFavoriteCreatedAt(
          fav.items.reduce((acc, i) => ({ ...acc, [i.ad_id]: i.created_at }), {} as Record<string, string>)
        );
      } catch {
        state.setFavoriteAdIds([]);
        state.setFavoriteCreatedAt({});
      }
      restorePostLoginPage();
    },
    [
      state.setUserId,
      state.setUserEmail,
      state.setUserName,
      state.setAuthEmailVerified,
      state.setUserProfilePicture,
      state.setIsLoggedIn,
      state.setUserProfile,
      state.setNeedsOnboarding,
      state.setFavoriteAdIds,
      state.setFavoriteCreatedAt,
      restorePostLoginPage,
    ]
  );

  // Handle onboarding completion: save profile via API, upload photo if provided, then set state and go home. Throws on API error.
  const handleOnboardingComplete = useCallback(
    async (data: OnboardingData, photoFile?: File) => {
      const me = await authClient.me();
      state.setAuthEmailVerified(me.email_verified);
      const completeProfile: OnboardingData = {
        ...data,
        email: state.userEmail,
        profileImage: data.profileImage || state.userProfilePicture,
        memberSince: new Date().toISOString().split("T")[0],
        isEmailVerified: me.email_verified,
        isPhoneVerified: false,
        isCpfVerified: false,
      };
      const payload = onboardingDataToProfilePatch(completeProfile);
      if (photoFile) {
        delete (payload as { profile_image_url?: string }).profile_image_url;
      }
      const saved = await authClient.saveProfile(payload);
      let profileAfter = saved;
      if (photoFile) {
        profileAfter = await authClient.uploadProfilePhoto(photoFile);
      }
      const name = state.userName;
      state.setUserProfile(
        profileToOnboardingData(profileAfter, state.userEmail ?? "", name, me.email_verified)
      );
      state.setUserProfilePicture(profileAfter.profile_image_url ?? "");
      const checkoutAddrs = profileAddressesToCheckoutAddresses(profileAfter.addresses ?? []);
      state.setAddresses(checkoutAddrs);
      const mainAddr = checkoutAddrs.find((a) => a.isDefault) ?? checkoutAddrs[0];
      state.setSelectedAddressId(mainAddr ? mainAddr.id : null);
      state.setNeedsOnboarding(false);
      restorePostLoginPage();
    },
    [
      state.userEmail,
      state.userName,
      state.userProfilePicture,
      state.setUserProfile,
      state.setUserProfilePicture,
      state.setAddresses,
      state.setSelectedAddressId,
      state.setNeedsOnboarding,
      restorePostLoginPage,
      state.setAuthEmailVerified,
    ]
  );

  // Handle onboarding skip
  const handleOnboardingSkip = useCallback(() => {
    state.setNeedsOnboarding(false);
    restorePostLoginPage();
  }, [state.setNeedsOnboarding, restorePostLoginPage]);

  // Handle edit profile save: PATCH /auth/me/profile, then refresh app profile state.
  const handleSaveProfile = useCallback(
    async (data: EditProfileFormData) => {
      const payload = editProfileDataToProfilePatch(data, state.userProfile);
      const profile = await authClient.saveProfile(payload);
      const me = await authClient.me();
      state.setAuthEmailVerified(me.email_verified);
      const name = data.fullName?.trim() || state.userName;
      state.setUserName(name);
      state.setUserProfile(profileToOnboardingData(profile, state.userEmail, name, me.email_verified));
      state.setUserProfilePicture(profile.profile_image_url ?? "");
    },
    [
      state.userProfile,
      state.userEmail,
      state.userName,
      state.setUserProfile,
      state.setUserProfilePicture,
      state.setUserName,
      state.setAuthEmailVerified,
    ]
  );

  // When My Addresses saves (edit/delete/set main/add), refresh app profile so default address is up to date.
  const handleProfileUpdated = useCallback(
    (profile: Profile) => {
      const name = state.userName;
      state.setUserProfile(
        profileToOnboardingData(profile, state.userEmail ?? "", name, state.authEmailVerified)
      );
      state.setUserProfilePicture(profile.profile_image_url ?? "");
      const checkoutAddrs = profileAddressesToCheckoutAddresses(profile.addresses ?? []);
      state.setAddresses(checkoutAddrs);
      const mainAddr = checkoutAddrs.find((a) => a.isDefault) ?? checkoutAddrs[0];
      state.setSelectedAddressId(mainAddr ? mainAddr.id : null);
    },
    [
      state.userEmail,
      state.userName,
      state.authEmailVerified,
      state.setUserProfile,
      state.setUserProfilePicture,
      state.setAddresses,
      state.setSelectedAddressId,
    ]
  );

  // When any auth request gets 401 and refresh fails (or no refresh token), clear session and show login.
  // If the user was on a protected page when the session died, save it as the pending auth intent so
  // `restorePostLoginPage` brings them back to the same screen after they sign in again.
  useEffect(() => {
    authClient.setOnSessionExpired(() => {
      if (requiresAuth(state.currentPage)) {
        savePendingAuthPage(state.currentPage);
      }
      clearSessionAppRoute();
      state.setIsLoggedIn(false);
      state.setUserProfile(null);
      state.setAuthEmailVerified(undefined);
      state.setUserProfilePicture("");
      state.setNeedsOnboarding(false); // do not show "complete profile" onboarding when logged out
      state.setCart([]);
      state.setShippingOptions([]);
      state.setSelectedShippingId(null);
      state.setHubDisplay(null);
      state.setCurrentPage("login");
    });
    return () => {
      authClient.setOnSessionExpired(null);
    };
  }, [
    state.currentPage,
    state.setIsLoggedIn,
    state.setUserProfile,
    state.setAuthEmailVerified,
    state.setUserProfilePicture,
    state.setNeedsOnboarding,
    state.setCart,
    state.setShippingOptions,
    state.setSelectedShippingId,
    state.setHubDisplay,
    state.setCurrentPage,
  ]);

  // Deep link: ?ad=<ad_id> when not handled by useAppState bootstrap (e.g. legacy bookmark).
  useEffect(() => {
    if (consumedAdDeepLinkRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const adId = parseAdIdFromDeepLinkParam(params.get("ad"));
    if (!adId) return;
    consumedAdDeepLinkRef.current = true;
    state.setSelectedAdId(adId);
    navigation.navigateToPage("ad-detail");
    removeAdDeepLinkFromAddressBar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for cold-load share links
  }, []);

  // Email verify / password reset: cold-start on /?after_email_verify=1 or ?after_password_reset=1 → login; strip params.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("after_email_verify") !== "1" && q.get("after_password_reset") !== "1") return;
      removeAuthLandingQueriesFromAddressBar();
    } catch {
      /* ignore */
    }
  }, []);

  // Auth-required route guard: anonymous visitors cannot stay on pages that
  // need user data (e.g. /offers/received, /purchases, cart, checkout). Save
  // their intent and redirect to login so `restorePostLoginPage` can bring
  // them back to the requested screen once signed in. Waits for
  // `authCheckPending` so stored sessions get a chance to hydrate first.
  useEffect(() => {
    if (state.authCheckPending) return;
    if (state.isLoggedIn) return;
    if (!requiresAuth(state.currentPage)) return;
    savePendingAuthPage(state.currentPage);
    setLoginDefaultSignUp(false);
    state.setCurrentPage("login");
  }, [state.authCheckPending, state.isLoggedIn, state.currentPage, state.setCurrentPage]);

  // Stripe redirect return (3DS, Pix, Boleto): backend finalize gate before confirmation (authoritative Stripe + idempotent patch).
  // Run once on app mount (URL-driven flow) to avoid cancellation on re-renders.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe_return") !== "1") return;
    const orderId = params.get("order_id");
    if (!orderId) return;
    if (processedStripeReturnOrderIdRef.current === orderId) return;
    processedStripeReturnOrderIdRef.current = orderId;
    suppressCartHydrationForStripeReturnRef.current = true;

    const paymentIntentHint =
      params.get("payment_intent")?.trim() ||
      params.get("payment_intent_id")?.trim() ||
      undefined;

    // Remove query params before async work so re-renders do not re-trigger this flow.
    const cleanUrl = window.location.pathname + window.location.hash || "";
    window.history.replaceState({}, "", cleanUrl);

    (async () => {
      try {
        const fin = await authClient.finalizePayment(orderId, paymentIntentHint ? { payment_intent_hint: paymentIntentHint } : undefined);
        // Full payment or swap first leg: same confirmation UX (order-confirmation polls swap_first_paid vs paid).
        if (fin.finalized || fin.awaiting_second_party) {
          await handlers.handlePaymentSuccess(orderId);
        } else {
          state.setSelectedOrderId(orderId);
          if (fin.can_retry_payment) {
            state.setPendingPaymentOrderId(orderId);
            toast.error("Pagamento não concluído. Você pode tentar novamente com outro método.");
          } else {
            toast.error("Pagamento não concluído.");
          }
          state.setCurrentPage("order-detail");
        }
      } catch {
        state.setSelectedOrderId(orderId);
        state.setCurrentPage("order-detail");
        toast.error("Não foi possível confirmar o pagamento agora. Tente novamente.");
      } finally {
        suppressCartHydrationForStripeReturnRef.current = false;
        if (authClient.getAccessToken()) {
          try {
            await hydrateBuyerCartFromServer({
              setCart: state.setCart,
              setCartOrderId: state.setCartOrderId,
              setShippingOptions: state.setShippingOptions,
              setSelectedShippingId: state.setSelectedShippingId,
              setHubDisplay: state.setHubDisplay,
            });
          } catch {
            state.setCart([]);
            state.setShippingOptions([]);
            state.setSelectedShippingId(null);
            state.setHubDisplay(null);
          }
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When opening cart and we have a pending order (cartOrderId) but cart is empty, restore cart/checkout from that order
  useEffect(() => {
    if (state.currentPage !== "cart" || !state.cartOrderId || state.cart.length > 0) return;
    handlers.restoreCheckoutFromOrder(state.cartOrderId).catch(() => {
      if (state.setCartOrderId) state.setCartOrderId(null);
    });
  }, [state.currentPage, state.cartOrderId, state.cart.length, state.setCartOrderId, handlers.restoreCheckoutFromOrder]);

  // Conta Digital return from Stripe (onboarding/update): open wallet tab so user sees success and refreshed data
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("wallet") !== "1") return;
    state.setCurrentPage("wallet");
  }, [state.setCurrentPage]);

  // Restore session on load when we have stored tokens
  useEffect(() => {
    if (!state.authCheckPending) return;
    const access = authClient.getAccessToken();
    const refresh = authClient.getRefreshToken();
    if (!access && !refresh) {
      state.setAuthCheckPending(false);
      return;
    }
    (async () => {
      try {
        let user;
        try {
          user = await authClient.me();
        } catch (e: unknown) {
          const err = e as { status?: number };
          if (err?.status === 401 && refresh) {
            await authClient.refresh();
            user = await authClient.me();
          } else {
            throw e;
          }
        }
        state.setUserId(user.user_id);
        state.setUserEmail(user.email);
        state.setUserName(user.name);
        state.setAuthEmailVerified(user.email_verified);
        state.setIsLoggedIn(true);
        try {
          const profile = await authClient.getProfile();
          state.setUserProfile(profileToOnboardingData(profile, user.email, user.name, user.email_verified));
          state.setUserProfilePicture(profile.profile_image_url ?? "");
          state.setNeedsOnboarding(false);
          const checkoutAddrs = profileAddressesToCheckoutAddresses(profile.addresses ?? []);
          state.setAddresses(checkoutAddrs);
          const mainAddr = checkoutAddrs.find((a) => a.isDefault) ?? checkoutAddrs[0];
          state.setSelectedAddressId(mainAddr ? mainAddr.id : null);
        } catch (e) {
          state.setUserProfile(null);
          state.setUserProfilePicture("");
          state.setNeedsOnboarding(isProfileNotFoundError(e));
        }
        // Hydrate favorites from API (ids + created_at)
        try {
          const fav = await authClient.getFavorites();
          state.setFavoriteAdIds(fav.items.map((i) => i.ad_id));
          state.setFavoriteCreatedAt(
            fav.items.reduce((acc, i) => ({ ...acc, [i.ad_id]: i.created_at }), {} as Record<string, string>)
          );
        } catch {
          state.setFavoriteAdIds([]);
          state.setFavoriteCreatedAt({});
        }
        // Notifications unread count for bell badge
        try {
          const count = await authClient.getNotificationsUnreadCount();
          state.setNotificationUnreadCount(count);
        } catch {
          state.setNotificationUnreadCount(0);
        }
        // Cart: load from backend (unless Stripe return is finalizing — that flow re-hydrates in finally to avoid stale GET winning the race)
        if (!suppressCartHydrationForStripeReturnRef.current) {
          try {
            await hydrateBuyerCartFromServer({
              setCart: state.setCart,
              setCartOrderId: state.setCartOrderId,
              setShippingOptions: state.setShippingOptions,
              setSelectedShippingId: state.setSelectedShippingId,
              setHubDisplay: state.setHubDisplay,
            });
          } catch {
            state.setCart([]);
            state.setShippingOptions([]);
            state.setSelectedShippingId(null);
            state.setHubDisplay(null);
          }
        }
      } catch {
        authClient.clearTokens();
      } finally {
        state.setAuthCheckPending(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  // Notification unread count: single refetch policy (visibility + open notifications page + optional interval).
  // All refetches go through refetchNotificationCount; no per-action refetches from offer/chat pages.
  useEffect(() => {
    if (!state.isLoggedIn) return;
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        refetchNotificationCount();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    refetchNotificationCount(); // once on login / when policy effect runs
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [state.isLoggedIn, refetchNotificationCount]);

  // Refetch when user opens the notifications page.
  useEffect(() => {
    if (state.isLoggedIn && state.currentPage === "notifications") {
      refetchNotificationCount();
    }
  }, [state.isLoggedIn, state.currentPage, refetchNotificationCount]);

  // Optional: refetch every 5 minutes while tab is visible.
  const NOTIFICATION_REFETCH_INTERVAL_MS = 5 * 60 * 1000;
  useEffect(() => {
    if (!state.isLoggedIn) return;
    if (document.visibilityState !== "visible") return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refetchNotificationCount();
    }, NOTIFICATION_REFETCH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [state.isLoggedIn, refetchNotificationCount]);

  // Chat / FAB unread: refetch conversation list when tab becomes visible (logged-in only).
  useEffect(() => {
    if (!state.isLoggedIn || !state.userId) return;
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        refetchConversationsList();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (document.visibilityState === "visible") {
      refetchConversationsList();
    }
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [state.isLoggedIn, state.userId, refetchConversationsList]);

  // Poll conversation list while tab is visible (updates floating chat badge when modal is closed).
  const CONVERSATIONS_UNREAD_POLL_MS = 45 * 1000;
  useEffect(() => {
    if (!state.isLoggedIn || !state.userId) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refetchConversationsList();
    }, CONVERSATIONS_UNREAD_POLL_MS);
    return () => clearInterval(id);
  }, [state.isLoggedIn, state.userId, refetchConversationsList]);

  useEffect(() => {
    if (!isProfileIncomplete(state.userProfile)) {
      setIncompleteProfileBannerDismissed(false);
    }
  }, [state.userProfile]);

  useEffect(() => {
    if (!state.isLoggedIn) setIncompleteProfileBannerDismissed(false);
  }, [state.isLoggedIn]);

  /** Full buyer/seller chrome (cart, favorites, wallet, menu, …) only after profile is complete. */
  const accountNavEnabled = useMemo(
    () => state.isLoggedIn && !isProfileIncomplete(state.userProfile),
    [state.isLoggedIn, state.userProfile]
  );

  const withAccountNav = (node: ReactNode) => (
    <AccountNavProvider enabled={accountNavEnabled}>{node}</AccountNavProvider>
  );

  // Show loading while checking stored session (avoids flash of login then app)
  if (state.authCheckPending) {
    return withAccountNav(
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  // Anonymous visitor on a protected page: the auth guard effect above will flip `currentPage` to
  // "login"; render a neutral placeholder for the brief commit window so the protected page never
  // flashes with empty data (e.g. "Nenhuma oferta encontrada" on /offers/received).
  if (!state.isLoggedIn && state.currentPage !== "login" && requiresAuth(state.currentPage)) {
    return withAccountNav(
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Redirecionando para login…</p>
      </div>
    );
  }

  // Show login page only when user explicitly navigates to it (e.g. clicks "Entrar"). Anonymous users can browse home and ad-detail.
  if (state.currentPage === "login" && !state.isLoggedIn) {
    return withAccountNav(
      <LoginPage
        onLogin={handleLogin}
        onSignUp={handleSignUp}
        onGoogleSSO={handleGoogleSSO}
        onAppleSSO={handleAppleSSO}
        onSignupSuccessRedirect={() => {
          state.setBrowsingAfterSignup(true);
          state.setCurrentPage("home");
        }}
        onForgotPasswordSuccessGoHome={() => {
          state.setBrowsingAfterSignup(true);
          state.setCurrentPage("home");
        }}
        onBrowseWithoutLogin={() => {
          clearPendingAuthPage();
          state.setCurrentPage("home");
        }}
        onLogoClick={() => state.setCurrentPage("home")}
        googleClientId={getAuthConfig().googleClientId}
        appleClientId={getAuthConfig().appleClientId}
        appleRedirectUri={getAuthConfig().appleRedirectUri || undefined}
        defaultSignUp={loginDefaultSignUp}
      />
    );
  }

  // Show onboarding if needed
  if (state.needsOnboarding) {
    return withAccountNav(
      <NewUserOnboarding
        email={state.userEmail}
        fullName={state.userName}
        profilePicture={state.userProfilePicture}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
    );
  }

  // Show non-home pages via router (ad-detail, category-results, etc.). Works for both anonymous and logged-in.
  if (state.currentPage !== "home" && state.currentPage !== "login") {
    return withAccountNav(
      <>
      <AppRouter
        currentPage={state.currentPage}
        commonPageProps={commonPageProps}
        ads={state.ads}
        searchResultsAds={state.searchResultsAds}
        searchNextCursor={state.searchNextCursor}
        searchResultsLoading={state.searchResultsLoading}
        loadSearchResults={state.loadSearchResults}
        loadMoreSearchResults={state.loadMoreSearchResults}
        myAdsForOffer={myAdsForOffer}
        myAdsForOfferLoading={myAdsForOfferLoading}
        selectedAdId={state.selectedAdId}
        selectedOrderId={state.selectedOrderId}
        orderDetailReturnPage={state.orderDetailReturnPage}
        selectedSellerName={state.selectedSellerName}
        selectedSellerId={state.selectedSellerId}
        favoriteItems={state.favoriteItems}
        favoriteAdIds={state.favoriteAdIds}
        favoriteCreatedAt={state.favoriteCreatedAt}
        cart={state.cart}
        addresses={state.addresses}
        selectedAddressId={state.selectedAddressId}
        shippingOptions={state.shippingOptions}
        shippingOptionsLoading={state.shippingOptionsLoading}
        shippingOptionsError={state.shippingOptionsError}
        loadShippingOptions={handlers.loadShippingOptions}
        hubDisplay={state.hubDisplay}
        selectedShippingId={state.selectedShippingId}
        paymentMethods={state.paymentMethods}
        selectedPaymentId={state.selectedPaymentId}
        selectedInstallments={state.selectedInstallments}
        lastOrderNumber={state.lastOrderNumber}
        orderError={state.orderError}
        orderConfirming={state.orderConfirming}
        pendingPaymentOrderId={state.pendingPaymentOrderId}
        pendingPaymentClientSecret={state.pendingPaymentClientSecret}
        pendingPaymentPublishableKey={state.pendingPaymentPublishableKey}
        onPaymentSuccess={handlers.handlePaymentSuccess}
        onBackFromStripeCheckout={handlers.handleBackFromStripeCheckout}
        onPayPendingOrder={handlers.handlePayPendingOrder}
        onGoToCheckoutToPayOrder={handlers.handleGoToCheckoutToPayOrder}
        onPayOrderFromReview={handlers.handlePayOrderFromReview}
        payOrderId={state.payOrderId}
        setPayOrderId={state.setPayOrderId}
        cartOrderId={state.cartOrderId}
        secondPartyPaymentOrderId={state.secondPartyPaymentOrderId}
        setSecondPartyPaymentOrderId={state.setSecondPartyPaymentOrderId}
        onSecondPartyPaymentIntentReady={(orderId, clientSecret, publishableKey) => {
          state.setPendingPaymentOrderId(orderId);
          state.setPendingPaymentClientSecret(clientSecret);
          state.setPendingPaymentPublishableKey(publishableKey);
          state.setCurrentPage("checkout-stripe");
        }}
        conversations={state.conversations}
        setConversations={state.setConversations}
        selectedConversationId={state.selectedConversationId}
        chatMessages={state.chatMessages}
        receivedOffers={state.receivedOffers}
        loadReceivedOffers={loadReceivedOffers}
        sentOffers={state.sentOffers}
        loadSentOffers={loadSentOffers}
        selectedOfferId={state.selectedOfferId}
        showCartToast={state.showCartToast}
        cartToastProduct={state.cartToastProduct}
        isModalOpen={state.isModalOpen}
        isMenuOpen={state.isMenuOpen}
        isChatModalOpen={state.isChatModalOpen}
        onOpenChatModal={handleChatClick}
        onCloseChatModal={() => state.setIsChatModalOpen(false)}
        totalUnreadMessages={totalUnreadMessages}
        notificationUnreadCount={state.notificationUnreadCount}
        onUnreadCountChange={state.setNotificationUnreadCount}
        onNotificationCountRefetch={undefined}
        onSetCurrentPage={state.setCurrentPage}
        onNavigateBack={handleBack}
        onSetReturnToPage={state.setReturnToPage}
        onNavigateToNotificationLink={handleNavigateToNotificationLink}
        onSetSelectedAdId={state.setSelectedAdId}
        onOpenOrderDetail={handleOpenOrderDetail}
        onSetSelectedOrderId={state.setSelectedOrderId}
        onSetSelectedConversationId={state.setSelectedConversationId}
        onToggleFavorite={(ad) => handlers.handleToggleFavorite(ad.id)}
        onRemoveFavorite={handlers.handleRemoveFavorite}
        onAddToCart={handlers.handleAddToCart}
        onRemoveFromCart={handlers.handleRemoveFromCart}
        isInCart={handlers.isInCart}
        isFavorite={handlers.isFavorite}
        onUpdateCartQuantity={handlers.handleUpdateCartQuantity}
        onSelectAddress={handlers.handleSelectAddress}
        onSelectShipping={handlers.handleSelectShipping}
        onShippingContinueToReview={handlers.handleContinueToReviewFromShipping}
        onSelectPayment={handlers.handleSelectPayment}
        onSelectInstallments={handlers.handleSelectInstallments}
        onAddNewPaymentMethod={handlers.handleAddNewPaymentMethod}
        onConfirmOrder={handlers.handleConfirmOrder}
        onChatWithSeller={handlers.handleChatWithSeller}
        onSendMessage={(_conversationId, message) => handlers.handleSendMessage(message)}
        onCloseCartToast={handleCloseCartToast}
        onCreateAd={handleCreateAdWithRefresh}
        onAdDeleted={() => {
          state.invalidateCategoriesWithAds();
          setMyAdsRefreshKey((k) => k + 1);
        }}
        onCloseModal={handleCloseModal}
        onOpenModal={handleOpenModal}
        myAdsRefreshKey={myAdsRefreshKey}
        onCloseMenu={handleCloseMenu}
        // Same shape as home `<PageLayout {...handlers.menuHandlers} />` so MenuDrawer receives onNavigateTo* props.
        menuHandlers={handlers.menuHandlers}
        onSaveProfile={handleSaveProfile}
        onProfileUpdated={handleProfileUpdated}
        searchQuery={state.searchQuery}
        onSearchChange={handleSearchChange}
        selectedCategory={state.selectedCategory}
        categoryDisplayName={
          state.categoryCatalog?.find((c) => c.category_id === state.selectedCategory)?.name ??
          state.selectedCategory
        }
        categoryCatalog={state.categoryCatalog}
        onCategoryChange={handleCategoryChange}
        categories={CATEGORIES}
        popularCategories={popularCategories}
        currentLocation={currentLocation}
        onLocationClick={handleLocationSelectorOpen}
        onSortClick={handleSortSheetOpen}
        onCustomFilterClick={handleCustomFilterOpen}
        activeFiltersCount={activeCustomFiltersCount}
        currentSort={state.currentSort}
        customFilters={state.customFilters}
        onBackToHome={handleBackToHome}
        isSortSheetOpen={state.isSortSheetOpen}
        onCloseSortSheet={handleSortSheetClose}
        onSortChange={state.setCurrentSort}
        isCustomFilterOpen={state.isCustomFilterOpen}
        onCloseCustomFilter={handleCustomFilterClose}
        onCustomFilterChange={state.setCustomFilters}
        onClearCustomFilters={handleClearCustomFilters}
        isLocationSelectorOpen={state.isLocationSelectorOpen}
        onCloseLocationSelector={handleLocationSelectorClose}
        selectedCity={state.selectedCity}
        onSelectLocation={state.setSelectedCity}
        onClearNavigatedFromMenu={handleClearNavigatedFromMenu}
        onNavigateBackToSettings={handleNavigateBackToSettings}
        onNavigateToPasswordSecurity={settingsNavigators.toPasswordSecurity}
        onNavigateToLanguageRegion={settingsNavigators.toLanguageRegion}
        onNavigateToAbout={settingsNavigators.toAbout}
        onNavigateToTerms={settingsNavigators.toTerms}
        onNavigateToPrivacyPolicy={settingsNavigators.toPrivacyPolicy}
        onNavigateToPrivacy={settingsNavigators.toPrivacy}
        onNavigateToAccountDeletionPublic={settingsNavigators.toAccountDeletionPublic}
        onMakeOffer={handleMakeOffer}
        onSubmitOffer={handleSubmitOffer}
        isSubmittingOffer={isSubmittingOffer}
        onViewOfferDetails={handleViewOfferDetails}
        userId={state.userId}
        userProfile={state.userProfile}
        userEmail={state.userEmail}
        userProfileImageUrl={state.userProfilePicture}
        authUserName={state.userName}
        isDarkMode={state.darkMode.isDarkMode}
        onToggleDarkMode={state.darkMode.toggleDarkMode}
      />
      {accountNavEnabled && (
        <ChatModal
          isOpen={state.isChatModalOpen}
          onClose={() => state.setIsChatModalOpen(false)}
          selectedConversationId={state.selectedConversationId}
          onSelectConversation={state.setSelectedConversationId}
          onBackToList={() => state.setSelectedConversationId(null)}
          conversations={state.conversations}
          setConversations={state.setConversations}
          chatMessages={state.chatMessages}
          onSendMessage={(_conversationId, message) => handlers.handleSendMessage(message)}
          onViewOfferDetails={handleViewOfferDetails}
          userId={state.userId}
          activeTab={(commonPageProps.activeTab === "wallet" ? "home" : commonPageProps.activeTab) as "home" | "favorites"}
          onTabChange={(tab) => navigation.navigateToPage(tab as PageType)}
          favoritesCount={commonPageProps.favoritesCount}
          onMenuClick={commonPageProps.onMenuClick}
          onPostAdClick={commonPageProps.onPostAdClick}
        />
      )}
      <CartBlockToast
        message={accountNavEnabled ? state.cartBlockMessage : null}
        onDismiss={() => state.setCartBlockMessage(null)}
        onGoToCart={() => {
          state.setCartBlockMessage(null);
          state.setCurrentPage("cart");
        }}
      />
      <OfferSentToast
        isOpen={showOfferSentToast}
        onClose={() => {
          offerSentOnCloseRef.current();
          setShowOfferSentToast(false);
        }}
      />
      <Toaster richColors position="top-center" />
      </>
    );
  }

  // Home page
  return withAccountNav(
    <>
      <PageLayout
        isModalOpen={state.isModalOpen}
        onCloseModal={handleCloseModal}
        onSubmitAd={handleCreateAdWithRefresh}
        isMenuOpen={state.isMenuOpen}
        onCloseMenu={handleCloseMenu}
        isLoggedIn={state.isLoggedIn}
        userName={state.userProfile?.preferredName?.trim() || state.userName || ""}
        userEmail={state.userEmail}
        userInitials={(() => {
          const full = (state.userProfile?.fullName ?? state.userName ?? "").trim();
          return full ? full.split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase() : "";
        })()}
        userProfileImageUrl={state.userProfilePicture}
        onLoginClick={() => {
          state.setIsMenuOpen(false);
          setLoginDefaultSignUp(false);
          state.setCurrentPage("login");
        }}
        {...handlers.menuHandlers}
      >
        <div className="min-h-screen bg-background">
          {/* Header */}
          <CommonHeader
            isLoggedIn={state.isLoggedIn}
            userName={state.userProfile?.preferredName?.trim() || state.userName || ""}
            currentAddress={state.userProfile?.address ? `${state.userProfile.address.street}, ${state.userProfile.address.number}` : ""}
            onLoginClick={openLoginSignIn}
            onSignUpClick={openLoginSignUp}
            cartCount={state.cart.length}
            notificationCount={state.notificationUnreadCount}
            favoritesCount={state.favoriteAdIds.length}
            onCartClick={handleCartClick}
            onNotificationsClick={handleNotificationsClick}
            onAddressClick={handleAddressClick}
            onMenuClick={() => state.setIsMenuOpen(true)}
            onFavoritesClick={() => navigation.navigateToPage("favorites")}
            onMyAdsClick={() => navigation.navigateToPage("my-ads")}
            onWalletClick={() => navigation.navigateToPage("wallet")}
            onLogoClick={handleBackToHome}
          />

          {state.isLoggedIn &&
            isProfileIncomplete(state.userProfile) &&
            !incompleteProfileBannerDismissed && (
              <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-3">
                <Alert className="border-primary/35 bg-primary/5 pr-12 relative">
                  <button
                    type="button"
                    onClick={() => setIncompleteProfileBannerDismissed(true)}
                    className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label="Fechar aviso"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <AlertTitle className="text-foreground">Complete seu cadastro</AlertTitle>
                  <AlertDescription className="text-muted-foreground flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mt-1">
                    <span>
                      Para anunciar, comprar e usar todos os recursos com segurança, precisamos de alguns dados
                      seus.
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0 w-fit"
                      onClick={() => state.setNeedsOnboarding(true)}
                    >
                      Completar cadastro
                    </Button>
                  </AlertDescription>
                </Alert>
              </div>
            )}

          {/* Home Search Bar */}
          <HomeSearchBar
            searchQuery={state.searchQuery}
            onSearchQueryChange={state.setSearchQuery}
            onSearch={handleHomeSearch}
            ads={state.ads}
          />

          {/* Main Content */}
          <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8">
            <PromoCarousel
              slides={promoShellSlides}
              onCategoryClick={handleCarouselCategoryClick}
              onNavigateToTrocaViaHub={handleCarouselNavigateToTrocaViaHub}
              validCatalogCategoryIds={validCatalogCategoryIdsForPromo}
            />
            
            <div className="flex items-center justify-between mb-6" ref={productsRef}>
              <p className="text-muted-foreground">
                {homeFeedLoading
                  ? "Carregando…"
                  : `${state.ads.length} ${state.ads.length === 1 ? "item disponível" : "itens disponíveis"}`}
              </p>
            </div>
            {homeFeedLoading ? (
              <div className="flex min-h-[200px] items-center justify-center">
                <p className="text-muted-foreground">Carregando anúncios…</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {state.ads.map((ad) => (
                  <AdCard 
                    key={ad.id} 
                    ad={ad} 
                    isFavorite={handlers.isFavorite(ad.id)}
                    onToggleFavorite={accountNavEnabled ? handlers.handleToggleFavorite : undefined}
                    onAdClick={() => handleAdClick(ad.id)}
                  />
                ))}
              </div>
            )}
          </main>

          {/* Bottom Navigation */}
          <BottomNav {...commonPageProps} />

          {/* Location Selector Modal */}
          <LocationSelectorModal
            isOpen={state.isLocationSelectorOpen}
            onClose={handleLocationSelectorClose}
            currentLocation={state.selectedCity || { name: "Franca", state: "São Paulo", stateCode: "SP" }}
            onSelectLocation={state.setSelectedCity}
          />
        </div>
      </PageLayout>

      {/* Floating chat only when profile complete (same chrome as logged-in hub) */}
      {accountNavEnabled && (
        <FloatingChatButton
          onClick={handleChatClick}
          unreadCount={totalUnreadMessages}
        />
      )}

      {/* Footer */}
      <Footer
        onLogoClick={handleBackToHome}
        onNavigateToAbout={settingsNavigators.toAbout}
        onNavigateToTerms={settingsNavigators.toTerms}
        onNavigateToPrivacyPolicy={settingsNavigators.toPrivacyPolicy}
        onNavigateToAccountDeletion={settingsNavigators.toAccountDeletionPublic}
        onNavigateToHelp={() => navigation.navigateToPage("help")}
        popularCategories={popularCategories}
        onCategoryClick={handleCarouselCategoryClick}
      />

      {/* Chat modal: open from chat icon or "Chat with seller", minimize to resume browsing */}
      {accountNavEnabled && (
        <ChatModal
          isOpen={state.isChatModalOpen}
          onClose={() => state.setIsChatModalOpen(false)}
          selectedConversationId={state.selectedConversationId}
          onSelectConversation={state.setSelectedConversationId}
          onBackToList={() => state.setSelectedConversationId(null)}
          conversations={state.conversations}
          setConversations={state.setConversations}
          chatMessages={state.chatMessages}
          onSendMessage={(_conversationId, message) => handlers.handleSendMessage(message)}
          onViewOfferDetails={handleViewOfferDetails}
          userId={state.userId}
          activeTab="home"
          onTabChange={(tab) => navigation.navigateToPage(tab as PageType)}
          favoritesCount={commonPageProps.favoritesCount}
          onMenuClick={commonPageProps.onMenuClick}
          onPostAdClick={commonPageProps.onPostAdClick}
        />
      )}

      <CartBlockToast
        message={accountNavEnabled ? state.cartBlockMessage : null}
        onDismiss={() => state.setCartBlockMessage(null)}
        onGoToCart={() => {
          state.setCartBlockMessage(null);
          state.setCurrentPage("cart");
        }}
      />
      <OfferSentToast
        isOpen={showOfferSentToast}
        onClose={() => {
          offerSentOnCloseRef.current();
          setShowOfferSentToast(false);
        }}
      />
      <Toaster richColors position="top-center" />
    </>
  );
}